import { useEffect, useState } from 'react'

import { formatAge, formatClock, NO_VALUE } from '../../lib/format.ts'

/**
 * A self-ticking age.
 *
 * Isolated into its own component with its own timer so that "há 6 s" advancing
 * does not re-render a single panel, let alone the tree. It is also the only
 * thing in the app allowed to update between stream events — because when the
 * stream stops, this number climbing is the signal that it stopped.
 */
export function RelativeTime({
  /** Epoch ms of the moment being aged, from the browser clock. */
  since,
  className = '',
  /** Refresh cadence. 1s while fresh matters; anything older can be coarse. */
  intervalMs = 1_000,
}: {
  since: number | null
  className?: string
  intervalMs?: number
}) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])

  if (since === null) return <span className={className}>{NO_VALUE}</span>

  return (
    <span className={`tnum ${className}`}>{formatAge(now - since)}</span>
  )
}

/** UTC wall clock of a server timestamp, with the zone stated. */
export function ServerClock({ iso, className = '' }: { iso: string | null; className?: string }) {
  return (
    <span className={`tnum ${className}`}>
      {formatClock(iso)}
      <span className="ml-1 text-fg-muted">UTC</span>
    </span>
  )
}
